import { collectLessonEndFeedbackThunk } from '../../../../../feedback/slices/feedback/thunks/collect-lesson-end-feedback.thunk.ts';
import { AppStateEnum } from '../../../../../main/types/app-state.types.ts';
import { ScriptKey, StepType, TaskType } from '../../../../../main/types/asset/asset.enums.ts';

import { selectCurrentLessonTaskStep } from '../../../../selectors/current-lesson-task-step.selector.ts';
import { selectCurrentTaskType } from '../../../../selectors/current-task-type.selector.ts';
import { updateAppStateThunk } from '../../../../slices/app-state/thunks/update-app-state.thunk.ts';
import { setBallIsVisible } from '../../../../slices/ball/ball.slice.ts';
import { completeStepThunk } from '../../../../slices/progress/thunks/complete-step.thunk.ts';
import {
  PICTURE_COMPREHENSION_LESSON_TASK_TYPES,
  SOUNDS_WITH_ARROW_LESSON_TASK_TYPES,
} from '../../../../types/task.types.ts';

import { BaseAppStateChangeHandlerUtil } from '../base-app-state-change.handler.ts';

/**
 * Handler for app speech tasks (when the app/critter speaks)
 * Used in various tasks including SayTheSounds, WordReading, PictureComprehension, and LessonComplete
 */
export class AppSayStateChangeHandlerUtil extends BaseAppStateChangeHandlerUtil {
  private static _instance: AppSayStateChangeHandlerUtil;

  static get instance(): AppSayStateChangeHandlerUtil {
    if (!AppSayStateChangeHandlerUtil._instance) {
      AppSayStateChangeHandlerUtil._instance = new AppSayStateChangeHandlerUtil();
    }
    return AppSayStateChangeHandlerUtil._instance;
  }

  protected getHandlerName(): string {
    return 'AppSay';
  }

  protected override async onReadyToStart(): Promise<void> {
    await super.onReadyToStart();
    console.log(`${this.getHandlerName()} onReadyToStart`);

    const dispatch = await this.reduxStoreUtil.getDispatch();

    this.resetBallPosition();

    // Play speech
    await this.audioPlayerUtil.speakStep();
    await this.audioPlayerUtil.waitSpeakCompletion();

    const nextState = await this.determineNextState();

    if (nextState) {
      if (nextState === AppStateEnum.ReadyForNextStep) {
        await dispatch(completeStepThunk()).unwrap();
      }
      await dispatch(updateAppStateThunk({ state: nextState, blockAnimation: true })).unwrap();
      return;
    }

    dispatch(setBallIsVisible(true));
  }

  protected override async onPlaying(): Promise<void> {
    console.log(`${this.getHandlerName()} onPlaying`);
    const dispatch = await this.reduxStoreUtil.getDispatch();

    this.resetBallPosition();

    // Play speech with phoneme-by-phoneme breakdown for AppSayFast
    await this.audioPlayerUtil.speakStep();
    await this.audioPlayerUtil.speakStepParts();
    await this.audioPlayerUtil.waitSpeakCompletion();

    // Complete the step and move to next state
    await dispatch(completeStepThunk()).unwrap();
    await dispatch(updateAppStateThunk({ state: AppStateEnum.ReadyForNextStep, blockAnimation: true })).unwrap();
  }

  protected override async onReadyForNextStep(): Promise<void> {
    await super.onReadyForNextStep();
    console.log(`${this.getHandlerName()} onReadyForNextStep`);

    const dispatch = await this.reduxStoreUtil.getDispatch();

    if (await this.isOneOfTasks(PICTURE_COMPREHENSION_LESSON_TASK_TYPES)) {
      dispatch(setBallIsVisible(true));
    } else {
      this.handleReadyForNextStep();
    }
  }

  protected override async onCollectingFeedback(): Promise<void> {
    await super.onCollectingFeedback();
    console.log(`${this.getHandlerName()} onCollectingFeedback`);

    const state = await this.reduxStoreUtil.getRootState();
    const taskType = selectCurrentTaskType(state);

    if (taskType === TaskType.LessonComplete) {
      const dispatch = await this.reduxStoreUtil.getDispatch();
      await dispatch(collectLessonEndFeedbackThunk()).unwrap();
      await dispatch(updateAppStateThunk({ state: AppStateEnum.ReadyForNextStep })).unwrap();
    }
  }

  /**
   * Handle Aborting state - interrupts speech
   */
  protected override async onAborting(): Promise<void> {
    console.log(`${this.getHandlerName()} onAborting`);
    this.audioPlayerUtil.stopPlaying();
    await super.onAborting();
  }

  private async determineNextState(): Promise<AppStateEnum | null> {
    const state = await this.reduxStoreUtil.getRootState();
    const { step } = selectCurrentLessonTaskStep(state);

    // Handle different flows based on task and step conditions
    const isAppSayInLessonComplete =
      (await this.isOneOfTasks([TaskType.LessonComplete])) && (await this.isOneOfSteps([StepType.AppSay]));
    if (isAppSayInLessonComplete) {
      return AppStateEnum.CollectingFeedback;
    } else if (
      (step.stepType === StepType.AppSayFast && step.scriptKey === ScriptKey.Repeat) ||
      (await this.isOneOfTasks([...PICTURE_COMPREHENSION_LESSON_TASK_TYPES, ...SOUNDS_WITH_ARROW_LESSON_TASK_TYPES]))
    ) {
      return AppStateEnum.ReadyForNextStep;
    }
    return null;
  }
}

export const getAppSayStateChangeHandlerUtil = (): AppSayStateChangeHandlerUtil =>
  AppSayStateChangeHandlerUtil.instance;
